import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemText,
  Typography,
  IconButton,
  Chip,
  Divider,
  Button,
  ListItemButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import InfiniteScroll from "react-infinite-scroll-component";
import styles from "./CountryCollaboratorList.module.scss";
import _ from "lodash";
import { ImageCheck, LoadingListEntityProfile } from "Explore/Components";
import { ImageCheckType } from "Explore/Config/ImageType";
import "flag-icons/css/flag-icons.min.css";

const CountryCollaboratorList = ({
  data,
  setData,
  fetchFunction,
  selectedKeywords,
  setSelectedKeywords,
  timeFilter,
  setTimeFilter,
}) => {
  const [listData, setListData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0,
  });
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const ref = useRef(null);

  const hasMore =
    (pagination.page + 1) * pagination.rowsPerPage < pagination.total;

  const closeDrawer = () => {
    setSelectedAffiliation(null);
    setData({});
    setListData({});
    setPagination({
      page: 0,
      rowsPerPage: 10,
      total: 0,
    });
    setIsLoading(false);
  };

  useEffect(() => {
    setListData(data || {});
    if (!!data?.id && !!fetchFunction) {
      setIsLoading(true);
      fetchFunction({
        id: data.id,
        page: 0,
        rowsPerPage: 10,
        keyword: selectedKeywords,
        timeFilter,
      }).then((res) => {
        setListData({ ...(data || {}), affiliations: res.results });
        setIsLoading(false);
        setPagination({
          page: 0,
          rowsPerPage: 10,
          total: res.total,
        });
      });
    }
  }, [data, selectedKeywords, timeFilter]);

  const handleLoadMore = () => {
    if (!!data?.id && !isLoading)
      fetchFunction({
        id: data.id,
        page: pagination.page + 1,
        rowsPerPage: pagination.rowsPerPage,
        keyword: selectedKeywords,
        timeFilter,
      }).then((res) => {
        setListData({
          ...data,
          affiliations: [...(listData?.affiliations || []), ...res.results],
        });
        setPagination({
          ...pagination,
          page: pagination.page + 1,
          total: res.total,
        });
      });
  };

  const handleGetAll = async ({ countryId, rowsPerPage = 500 }) => {
    if (!fetchFunction) {
      return listData?.affiliations || [];
    }
    const res = await fetchFunction({
      id: countryId,
      page: 0,
      rowsPerPage,
      keyword: selectedKeywords,
      timeFilter,
    });
    return res.results;
  };

  const selectKeyword = (keyword) => {
    const newSelectedKeywords = [...(selectedKeywords || [])];
    const _kw = newSelectedKeywords.findIndex((item) => item === keyword);
    if (_kw !== -1) {
      newSelectedKeywords.splice(_kw, 1);
    } else {
      newSelectedKeywords.push(keyword);
    }
    setSelectedKeywords(newSelectedKeywords);
  };

  return (
    <div
      className={styles["container"]}
      style={_.isEmpty(listData) ? { display: "none" } : {}}
    >
      <Drawer
        anchor='right'
        open={!_.isEmpty(listData)}
        onClose={closeDrawer}
        className={styles["drawer"]}
        classes={{
          paper: styles["drawer-paper"],
        }}
      >
        <Box
          className={styles["drawer-container"]}
          ref={ref}
          id='drawer-container'
        >
          <Box className={styles["drawer-header"]}>
            <Typography variant='h6'>
              <span className={`fi fi-${listData?.id?.toLowerCase()}`}></span>{" "}
              {listData?.name}
              {selectedKeywords?.length > 0 &&
                " - " + selectedKeywords.join(", ")}
            </Typography>
            <IconButton onClick={closeDrawer} className={styles["close-button"]}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />

          <Box className={styles["drawer-content"]}>
            <Box className={styles["title-container"]}>
              <Box>
                <Typography
                  variant='h6'
                  component='h1'
                  className={styles["title"]}
                >
                  Collaborator Institutions in {listData?.name}
                </Typography>
                <Typography variant='subtitle2'>
                  Total Publications: {listData?.num_pubs}
                </Typography>
              </Box>
            </Box>
            {listData?.common_keywords?.length > 0 && (
              <>
                <Typography
                  variant='subtitle2'
                  className={styles["keywords-title"]}
                >
                  Common Research Keywords:
                </Typography>
                <Box className={styles["keywords-container"]}>
                  {listData?.common_keywords?.map((keyword, index) => (
                    <Chip
                      key={index}
                      label={keyword}
                      className={styles["keyword-chip"]}
                      color='primary'
                      variant={
                        selectedKeywords?.includes(keyword)
                          ? "filled"
                          : "outlined"
                      }
                      clickable
                      onClick={() => {
                        selectKeyword(keyword);
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>

          {selectedAffiliation ? (
            <Box className={styles["affiliation-details"]}>
              <Box className={styles["detail-header"]}>
                <SchoolIcon className={styles["detail-icon"]} />
                <Typography variant='h6' className={styles["detail-title"]}>
                  {selectedAffiliation.institution_name}
                </Typography>
              </Box>

              <Typography variant='body1' className={styles["pub-count"]}>
                Publications: {selectedAffiliation.num_pubs}
              </Typography>

              {selectedAffiliation.match_score !== undefined && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, textAlign: 'center', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>Match Score</Typography>
                    <Typography variant="subtitle1" sx={{ color: '#0f3460', fontWeight: 700, lineHeight: 1 }}>
                      {Number(selectedAffiliation.match_score).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, textAlign: 'center', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>FWCI</Typography>
                    <Typography variant="subtitle1" sx={{ color: '#0f3460', fontWeight: 700, lineHeight: 1 }}>
                      {selectedAffiliation.avg_fwci}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, textAlign: 'center', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>Shared KWs</Typography>
                    <Typography variant="subtitle1" sx={{ color: '#0f3460', fontWeight: 700, lineHeight: 1 }}>
                      {selectedAffiliation.overlapping_kws_count}
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedAffiliation.common_keywords &&
                selectedAffiliation.common_keywords.length > 0 && (
                  <>
                    <Typography
                      variant='subtitle1'
                      className={styles["keywords-subtitle"]}
                    >
                      Research Keywords:
                    </Typography>
                    <Box className={styles["detail-keywords"]}>
                      {selectedAffiliation.common_keywords.map(
                        (keyword, idx) => (
                          <Chip
                            key={idx}
                            label={keyword}
                            className={styles["detail-keyword-chip"]}
                            color='primary'
                            variant={
                              selectedKeywords?.includes(keyword)
                                ? "filled"
                                : "outlined"
                            }
                            clickable
                            onClick={() => {
                              selectKeyword(keyword);
                            }}
                          />
                        )
                      )}
                    </Box>
                  </>
                )}

              {selectedAffiliation.collab_units &&
                selectedAffiliation.collab_units.length > 0 && (
                  <>
                    <Typography
                      variant='subtitle1'
                      className={styles["section-title"]}
                    >
                      Collaborating Units:
                    </Typography>
                    <List className={styles["collab-list"]}>
                      {selectedAffiliation.collab_units.map((unit, idx) => (
                        <ListItemButton
                          href={`/explore/institution/${unit.id}`}
                          key={idx}
                          className={styles["collab-item"]}
                        >
                          <Box className={styles["collab-avatar"]}>
                            {unit.photo_url ? (
                              <ImageCheck
                                url={unit.photo_url || "/placeholder.svg"}
                                alt={unit.name}
                                className={styles["unit-logo"]}
                                type={ImageCheckType.INSTITUTION_LOGO}
                              />
                            ) : (
                              <Box className={styles["avatar-placeholder"]}>
                                {unit.name.charAt(0)}
                              </Box>
                            )}
                          </Box>
                          <ListItemText
                            primary={unit.name}
                            secondary={`Publications: ${unit.num_pubs}`}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}

              <Button
                variant='outlined'
                onClick={() => setSelectedAffiliation(null)}
                className={styles["back-button"]}
              >
                Back to List
              </Button>
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={listData?.affiliations?.length || 0}
              next={handleLoadMore}
              hasMore={hasMore}
              loader={null}
              scrollableTarget='drawer-container'
            >
              <LoadingListEntityProfile
                isLoading={isLoading}
                component={
                  <List className={styles["affiliations-list"]}>
                    {listData?.affiliations?.map((affiliation, index) => (
                      <React.Fragment key={index}>
                        <ListItemButton
                          className={styles["affiliation-item"]}
                          style={{
                            backgroundColor:
                              index % 2 === 0
                                ? "var(--background-color-2)"
                                : "var(--background-color-1)",
                          }}
                          onClick={() => {
                            setSelectedAffiliation(affiliation);
                          }}
                        >
                          <Box className={styles["affiliation-item-header"]}>
                            <SchoolIcon className={styles["school-icon"]} />
                            <ListItemText
                              primary={affiliation.institution_name}
                              secondary={
                                affiliation.match_score !== undefined ? (
                                  <Stack direction="row" spacing={1} alignItems="center" divider={<span style={{ color: '#cbd5e1' }}>•</span>} sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#475569' }}>
                                      <strong style={{ fontWeight: 600, color: '#334155' }}>Match Score:</strong> <span style={{ color: '#2563eb', fontWeight: 500 }}>{Number(affiliation.match_score).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#475569' }}>
                                      <strong style={{ fontWeight: 600, color: '#334155' }}>FWCI:</strong> <span style={{ color: '#2563eb', fontWeight: 500 }}>{affiliation.avg_fwci}</span>
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#475569' }}>
                                      <strong style={{ fontWeight: 600, color: '#334155' }}>Shared KWs:</strong> <span style={{ color: '#2563eb', fontWeight: 500 }}>{affiliation.overlapping_kws_count}</span>
                                    </Typography>
                                  </Stack>
                                ) : undefined
                              }
                            />
                            <Chip
                              label={`Publications: ${affiliation.num_pubs}`}
                              color='primary'
                              className={styles["pub-count-chip"]}
                            />
                          </Box>

                          {affiliation.common_keywords?.length > 0 && (
                            <>
                              <Typography
                                variant='subtitle1'
                                className={styles["keywords-subtitle"]}
                              >
                                Research Keywords:
                              </Typography>
                              <Box className={styles["detail-keywords"]}>
                                {affiliation.common_keywords.map(
                                  (keyword, idx) => (
                                    <Chip
                                      key={idx}
                                      label={keyword}
                                      className={styles["detail-keyword-chip"]}
                                      variant={
                                        selectedKeywords?.includes(keyword)
                                          ? "filled"
                                          : "outlined"
                                      }
                                      size='small'
                                    />
                                  )
                                )}
                              </Box>
                            </>
                          )}
                        </ListItemButton>
                        {index < listData?.affiliations?.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                }
                componentNum={3}
                rows={3}
                avatar={false}
              />
            </InfiniteScroll>
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export default CountryCollaboratorList;
